import { z } from 'zod';
import { LowercaseUuidSchema, PaginationQuerySchema, createPaginatedResponseSchema } from './common.js';
/** What a connector may do. Read only for now, so this is the only scope a user can grant. */
export const ConnectorScopeSchema = z.enum(['bill:read']);
// Generous bounds that still stop an oversized query reaching the domain.
const OAUTH_VALUE_MAX = 200;
const OAUTH_URL_MAX = 2048;
/**
 * An OAuth authorisation request (RFC 6749 §4.1.1, RFC 7636 §4.3, RFC 8707),
 * under its wire names so the consent page can forward the query it was opened
 * with. Everything except the client and its redirect is optional here: the
 * domain reports a missing or wrong value to the client's redirect, which a
 * schema rejection could not do.
 *
 * Loose because the assistant owns this payload, and RFC 6749 §4.1.1 requires
 * unknown parameters to be ignored rather than refused.
 */
export const ConnectedAppAuthorizationRequestSchema = z.looseObject({
    client_id: z.string().min(1).max(OAUTH_URL_MAX),
    redirect_uri: z.string().min(1).max(OAUTH_URL_MAX),
    response_type: z.string().max(OAUTH_VALUE_MAX).optional(),
    code_challenge: z.string().max(OAUTH_VALUE_MAX).optional(),
    code_challenge_method: z.string().max(OAUTH_VALUE_MAX).optional(),
    scope: z.string().max(OAUTH_VALUE_MAX).optional(),
    state: z.string().max(OAUTH_URL_MAX).optional(),
    resource: z.string().max(OAUTH_URL_MAX).optional(),
});
/**
 * `valid`: show the consent page. `redirect-error`: the client should hear about
 * the problem, so send the browser to `redirectUrl`. Both URLs carry the RFC 9207
 * `iss` parameter.
 */
export const ConnectedAppAuthorizationResolutionSchema = z.discriminatedUnion('kind', [
    z.object({
        kind: z.literal('valid'),
        client: z.object({
            /** The host of the client's `client_id` URL: the name the user sees. */
            host: z.string(),
            redirectHost: z.string(),
            /** The code goes to a program on the user's own computer: the page warns about this. */
            loopbackRedirect: z.boolean(),
        }),
        scopes: z.array(ConnectorScopeSchema),
        denyRedirectUrl: z.string(),
    }),
    z.object({
        kind: z.literal('redirect-error'),
        redirectUrl: z.string(),
    }),
]);
export const CreateConnectedAppGrantInputSchema = z.strictObject({
    authorizationRequest: ConnectedAppAuthorizationRequestSchema,
    /** The one account the assistant may act on. The caller must be a member of it. */
    accountId: LowercaseUuidSchema,
    idempotencyKey: z.string().min(1).max(255),
});
export const CreateConnectedAppGrantResponseSchema = z.object({
    /** Where to send the browser: the client's redirect with a single-use code and `iss`. */
    redirectUrl: z.string(),
});
/** A live connection, as the person who made it or an owner of its account sees it. */
export const ConnectedAppGrantSchema = z.object({
    id: z.string(),
    clientName: z.string(),
    accountId: z.string(),
    connectedBy: z.object({ userId: z.string(), name: z.string() }),
    scopes: z.array(ConnectorScopeSchema),
    createdAt: z.iso.datetime(),
    lastUsedAt: z.iso.datetime().nullable(),
});
export const ConnectedAppGrantListResponseSchema = createPaginatedResponseSchema(ConnectedAppGrantSchema);
export const ListMyConnectedAppsQuerySchema = z.strictObject({
    ...PaginationQuerySchema.shape,
    accountId: LowercaseUuidSchema.optional(),
});
export const ListAccountConnectedAppsQuerySchema = z.strictObject(PaginationQuerySchema.shape);
export const AccountConnectedAppsParamsSchema = z.object({ accountId: LowercaseUuidSchema });
export const ConnectedAppGrantParamsSchema = z.object({ grantId: LowercaseUuidSchema });
export const RevokeConnectedAppGrantResponseSchema = z.object({ success: z.boolean() });
// The OAuth wire protocol. Snake case is the protocol's. Every request is loose
// because RFC 6749 §3.2 and RFC 7009 §2.1 have unknown parameters ignored.
export const OAuthGrantTypeSchema = z.enum(['authorization_code', 'refresh_token']);
/** RFC 7636 §4.1: 43 to 128 unreserved characters. */
const CodeVerifierSchema = z.string().regex(/^[A-Za-z0-9\-._~]{43,128}$/);
/** RFC 6749 §4.1.3 and §6. A public client identifies itself with `client_id`. */
export const OAuthTokenRequestSchema = z.discriminatedUnion('grant_type', [
    z.looseObject({
        grant_type: z.literal('authorization_code'),
        client_id: z.string().min(1).max(OAUTH_URL_MAX),
        code: z.string().min(1).max(OAUTH_VALUE_MAX),
        code_verifier: CodeVerifierSchema,
        redirect_uri: z.string().min(1).max(OAUTH_URL_MAX),
        resource: z.string().max(OAUTH_URL_MAX).optional(),
    }),
    z.looseObject({
        grant_type: z.literal('refresh_token'),
        client_id: z.string().min(1).max(OAUTH_URL_MAX),
        refresh_token: z.string().min(1).max(OAUTH_VALUE_MAX),
        resource: z.string().max(OAUTH_URL_MAX).optional(),
    }),
]);
/** RFC 7009 §2.1. There is no need for `token_type_hint`, because every token is looked up. */
export const OAuthRevocationRequestSchema = z.looseObject({
    token: z.string().min(1).max(OAUTH_URL_MAX * 2),
    client_id: z.string().min(1).max(OAUTH_URL_MAX),
});
export const E2EPrimeClientMetadataInputSchema = z.strictObject({
    clientId: z.string().min(1),
    redirectUris: z.array(z.string()).min(1),
});
export const E2EPrimeClientMetadataResponseSchema = z.object({
    /** The `resource` an authorisation request must name in this environment. */
    resource: z.string(),
});
export const E2EConnectedAppGrantCountQuerySchema = z.strictObject({ accountId: z.uuid() });
export const E2EConnectedAppGrantCountResponseSchema = z.object({ count: z.int().nonnegative() });
